/*
 * Clients: the list (tiles, chips, search, tick boxes with bulk changes and Undo), one client's page (read first: contacts,
 * folded properties, jobs, visits, notes, files; Deactivate and Merge sheets), and the client form.
 */
import { useCardLink } from "../api/money";
import { useNavigate } from "@tanstack/react-router";
import {
  BULK_LIMIT,
  CLIENT_TITLES,
  CONTACT_TYPE_WORDS,
  EMAIL_TYPES,
  fullAddress,
  INACTIVE_REASON_WORDS,
  INACTIVE_REASONS,
  LEAD_SOURCES,
  PHONE_TYPES,
  stateCode,
  type ClientDetail,
  type ClientSummary,
  type ClientRow,
  type ContactRow,
  type ContactType,
  type InactiveReason,
  type PropertyInput,
  type PropertyRow,
} from "@steward/shared";
import { useRef, useState, type ReactNode, type SubmitEvent } from "react";
import { ApiError, downloadFile } from "../api/client";
import {
  useAddContact,
  useBulkClients,
  useClient,
  useClientPages,
  useClients,
  useClientSummary,
  useCreateClient,
  useDeactivateClient,
  useMergeClient,
  useReactivateClient,
  useRemoveContact,
  useSaveProperty,
  useStartThread,
  useUpdateClient,
  type BulkInput,
  type ClientsQuery,
} from "../api/hooks";
import { useSession } from "../app/session";
import { AddressFields, draftOf, partsOf, type AddressDraft } from "../components/SubLocations";
import { DataTable } from "../components/DataTable";
import { Choice, Combobox, FormField, InputCount, InputText } from "../components/fields";
import { PlanOfferSend } from "../components/PlanOffer";
import { daysAway, dayWords, moneyExact, phoneText, plural, shortDate, todayIn, trend } from "../components/format";
import { Icon } from "../components/Icon";
import { PhoneRecord } from "../components/PhoneRecord";
import { usePhone } from "../components/usePhone";
import { markFresh, NextStep } from "../components/next";
import { Menu, Modal, useToast } from "../components/overlays";
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  Skeleton,
  StatusLabel,
  Switch,
} from "../components/parts";
import { errText, FilesCard, NotesCard } from "../components/work";
import {
  ClientBilling,
  ClientFacts,
  ClientSchedule,
  CreateForClient,
  PaymentMethods,
  RecentPricing,
  WorkOverview,
} from "./ClientCards";
import { ClientInbox } from "./Inbox";

export const LEAD_WORDS: Record<string, string> = {
  google: "Google search",
  lsa: "Google ads (LSA)",
  facebook: "Facebook",
  referral: "Referral",
  website: "Website",
  yard_sign: "Yard sign",
  repeat: "Past client",
  jobber: "Moved from Jobber",
  other: "Other",
};

// List ---------------------------------------------------------------------------------------------------------------

type SortKey = NonNullable<ClientsQuery["sort"]>;
/** Column key to the server's sort, for the sortable headers. */
const SORT_OF: Record<string, SortKey> = {
  name: "name",
  next: "next_visit",
  activity: "last_activity",
  lifetime: "lifetime",
};
const USUAL_DIR: Record<SortKey, "asc" | "desc"> = {
  name: "asc",
  next_visit: "asc",
  newest: "desc",
  last_activity: "desc",
  lifetime: "desc",
};
const SORT_WORDS: [SortKey, string][] = [
  ["name", "A to Z"],
  ["newest", "Newest first"],
  ["next_visit", "Next visit first"],
  ["last_activity", "Last activity"],
  ["lifetime", "Paid the most"],
];
type Show = NonNullable<ClientsQuery["show"]>;
const SHOW_WORDS: [Show, string][] = [
  ["plan", "On a plan"],
  ["nothing_booked", "Nothing booked"],
  ["at_risk", "At risk"],
];

/** The company's date for a moment, to say "4 days ago" on the company's clock. */
const dayIn = (iso: string, tz: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso));

/**
 * The client list (2026-10-29, the best of Jobber's and the old Steward's): tiles that say how the client base is doing
 * and filter the list, then one table with what the office looks for (who, where, tags, next visit, last activity, what
 * they paid us) and an at risk line under the name.
 */
export function ClientsList() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState<ClientsQuery>({ status: "active", sort: "last_activity" });
  const [typed, setTyped] = useState("");
  const list = useClientPages(company.id, { ...q, q: typed.trim() || undefined });
  const summary = useClientSummary(company.id);
  const bulk = useBulkClients();
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ input: BulkInput; message: string; token: string; undo: boolean } | null>(null);
  const [tagging, setTagging] = useState(false);
  const [tag, setTag] = useState("");
  const canBulk = can("client.bulk");
  const s = summary.data;
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const today = todayIn(company.timezone);
  const sort = q.sort ?? "last_activity";
  const dir = q.dir ?? USUAL_DIR[sort];
  const narrowed = typed.trim() !== "" || q.tag !== undefined || q.show !== undefined;
  const total =
    !s || narrowed ? undefined : q.status === "inactive" ? s.inactive : q.status === "all" ? s.active + s.inactive : s.active;
  const pick = (next: ClientsQuery) => {
    setQ(next);
    setTicked(new Set());
  };
  const showOnly = (v: Show) => pick({ ...q, show: q.show === v ? undefined : v, ...(q.show === v ? {} : { status: "active" }) });

  const opposite = (i: BulkInput): BulkInput => ({
    ids: i.ids,
    action:
      i.action === "deactivate" ? "reactivate" : i.action === "reactivate" ? "deactivate" : i.action === "tag" ? "untag" : "tag",
    ...(i.tag ? { tag: i.tag } : {}),
    ...(i.action === "reactivate" ? { reason: "other" as const } : {}),
  });
  const words = (i: BulkInput, n: number) =>
    i.action === "deactivate"
      ? `${plural(n, "client")} set to not active`
      : i.action === "reactivate"
        ? `${plural(n, "client")} active again`
        : i.action === "tag"
          ? `Tag ${i.tag ?? ""} added to ${plural(n, "client")}`
          : `Tag ${i.tag ?? ""} taken off ${plural(n, "client")}`;
  /** Runs a bulk change. Over the limit the server asks to confirm first; the confirm step shows its words. */
  const run = (input: BulkInput, undo = false) =>
    bulk.mutate(input, {
      onSuccess: (a) => {
        setConfirm(null);
        setTicked(new Set());
        if (undo) toast("Undone");
        else toast(words(input, a.done), { undo: () => run(opposite(input), true) });
      },
      onError: (e) => {
        if (e instanceof ApiError && e.code === "confirm_needed" && typeof e.extra.confirm === "string")
          setConfirm({ input, message: e.message, token: e.extra.confirm, undo });
        else toast(errText(e), { kind: "error" });
      },
    });
  const ids = [...ticked];

  return (
    <Page
      title="Clients"
      primaryAction={
        can("client.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/clients/new" })}>
            New client
          </Button>
        ) : null
      }
      moreActions={
        canBulk ? (
          <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
            {(close) => (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    void navigate({ to: "/settings/data" });
                  }}
                >
                  Import clients
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    void downloadFile("export/clients.csv", "clients.csv").then((ok) => {
                      if (!ok) toast("The preview has no server, so there is no file to download.");
                    });
                  }}
                >
                  Export clients
                </button>
              </>
            )}
          </Menu>
        ) : null
      }
    >
      <ClientTiles s={s} show={q.show} onShow={showOnly} />
      <DataTable<ClientRow>
        title={q.show ? (SHOW_WORDS.find(([v]) => v === q.show)?.[1] ?? "Clients") : "All clients"}
        rows={rows}
        total={total}
        rowKey={(c) => c.id}
        loading={list.isFetching && !list.isFetchingNextPage}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Name, phone, email or street" }}
        onRowClick={(c) => void navigate({ to: "/clients/$id", params: { id: c.id } })}
        {...(canBulk ? { selected: ticked, onSelect: setTicked } : {})}
        serverSort={{
          keys: Object.keys(SORT_OF),
          key: Object.keys(SORT_OF).find((k) => SORT_OF[k] === sort) ?? "",
          dir,
          onSort: (k) => {
            const next = SORT_OF[k] ?? "name";
            pick(next === sort ? { ...q, dir: dir === "asc" ? "desc" : "asc" } : { ...q, sort: next, dir: undefined });
          },
        }}
        footer={
          list.hasNextPage ? (
            <div className="table-more">
              <Button type="tertiary" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                Show more
              </Button>
            </div>
          ) : null
        }
        bulk={
          <>
            <Button type="secondary" size="small" icon="tag" onClick={() => setTagging(true)}>
              Add tag
            </Button>
            {q.status === "inactive" ? (
              <Button size="small" loading={bulk.isPending} onClick={() => run({ ids, action: "reactivate" })}>
                Make active
              </Button>
            ) : (
              <Button
                size="small"
                variation="destructive"
                loading={bulk.isPending}
                onClick={() => run({ ids, action: "deactivate", reason: "other" })}
              >
                Set not active
              </Button>
            )}
          </>
        }
        tools={
          <>
            <div className="chips">
              {(
                [
                  ["active", "Active", s?.active],
                  ["inactive", "Not active", s?.inactive],
                  ["all", "All", s ? s.active + s.inactive : undefined],
                ] as const
              ).map(([v, label, count]) => (
                <Chip
                  key={v}
                  pressed={q.status === v}
                  onClick={() => pick({ ...q, status: v, ...(v === "active" ? {} : { show: undefined }) })}
                  {...(count !== undefined ? { count } : {})}
                >
                  {label}
                </Chip>
              ))}
              <Chip pressed={q.show === "nothing_booked"} onClick={() => showOnly("nothing_booked")}>
                Nothing booked
              </Chip>
              {s?.tags.length ? (
                <Menu
                  button={({ toggle }) => (
                    <Chip pressed={Boolean(q.tag)} onClick={toggle}>
                      {q.tag ? `Tag ${q.tag}` : "Tag"}
                    </Chip>
                  )}
                >
                  {(close) => (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          pick({ ...q, tag: undefined });
                          close();
                        }}
                      >
                        Every tag
                      </button>
                      {s.tags.map((t) => (
                        <button
                          key={t.tag}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            pick({ ...q, tag: t.tag });
                            close();
                          }}
                        >
                          {t.tag} <span className="muted num">{t.count}</span>
                        </button>
                      ))}
                    </>
                  )}
                </Menu>
              ) : null}
            </div>
            <Menu
              align="right"
              button={({ toggle }) => (
                <Button type="tertiary" size="small" icon="list" onClick={toggle}>
                  {SORT_WORDS.find(([v]) => v === sort)?.[1] ?? "Last activity"}
                </Button>
              )}
            >
              {(close) =>
                SORT_WORDS.map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      pick({ ...q, sort: v, dir: undefined });
                      close();
                    }}
                  >
                    {label}
                  </button>
                ))
              }
            </Menu>
          </>
        }
        empty={
          q.show === "at_risk" ? (
            "Nobody looks at risk right now."
          ) : q.show ? (
            "No clients match this."
          ) : q.status === "inactive" ? (
            "No clients are set to not active."
          ) : (
            <EmptyState icon="clients" title="No clients yet">
              Add your first client, or move them over from Jobber.
            </EmptyState>
          )
        }
        rowClass={(c) => (c.active ? undefined : "inactive")}
        columns={[
          {
            key: "name",
            header: "Name",
            render: (c) => (
              <div className="name-cell">
                <b>{c.name}</b>
                <span className="muted">{c.isCompany ? "Business" : (phoneText(c.phone) ?? c.email ?? "")}</span>
                {c.atRisk && c.active ? (
                  <span className="at-risk" title="At risk">
                    <Icon name="warning" size={14} />
                    {c.atRisk}
                  </span>
                ) : null}
              </div>
            ),
          },
          { key: "address", header: "Address", render: (c) => c.address ?? <span className="muted">No address</span> },
          {
            key: "service",
            header: "Service",
            render: (c) =>
              c.service ? (
                <div className="name-cell">
                  <span>{c.category ?? c.service}</span>
                  {c.category && c.category.toLowerCase() !== c.service.toLowerCase() ? (
                    <span className="muted">{c.service}</span>
                  ) : null}
                </div>
              ) : (
                <span className="muted">No jobs yet</span>
              ),
          },
          {
            key: "tags",
            header: "Tags",
            render: (c) =>
              c.tags.length ? (
                <div className="tag-list">
                  {c.tags.slice(0, 2).map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                  {c.tags.length > 2 ? (
                    <span className="tag" title={c.tags.slice(2).join(", ")}>
                      +{c.tags.length - 2}
                    </span>
                  ) : null}
                </div>
              ) : null,
          },
          {
            key: "next",
            header: "Next visit",
            render: (c) =>
              c.active ? (
                c.nextVisit ? (
                  <span title={shortDate(c.nextVisit)}>{dayWords(c.nextVisit, today)}</span>
                ) : c.lastVisit ? (
                  // Work done but nothing next (a one-time clean): say when, so it does not read as never served.
                  <span className="muted" title={`Nothing booked after ${shortDate(c.lastVisit)}`}>
                    Last visit {dayWords(c.lastVisit, today)}
                  </span>
                ) : (
                  <span className="muted">Nothing booked</span>
                )
              ) : (
                <StatusLabel status="inactive">
                  {c.inactiveReason ? INACTIVE_REASON_WORDS[c.inactiveReason] : "Not active"}
                </StatusLabel>
              ),
          },
          {
            key: "activity",
            header: "Last activity",
            render: (c) =>
              c.lastActivity ? (
                <span title={new Date(c.lastActivity.at).toLocaleString()}>
                  {c.lastActivity.what}{" "}
                  <span className="muted">{daysAway(dayIn(c.lastActivity.at, company.timezone), today).toLowerCase()}</span>
                </span>
              ) : (
                <span className="muted">Nothing yet, added {shortDate(c.createdAt.slice(0, 10))}</span>
              ),
          },
          {
            key: "lifetime",
            header: "Lifetime",
            align: "right",
            render: (c) => <span className="num">{moneyExact(c.lifetimeCents)}</span>,
          },
        ]}
      />
      <Modal
        title="Add a tag"
        open={tagging}
        onClose={() => setTagging(false)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setTagging(false)}>
              Cancel
            </Button>
            <Button
              disabled={!tag.trim()}
              loading={bulk.isPending}
              onClick={() => {
                setTagging(false);
                run({ ids, action: "tag", tag: tag.trim() });
                setTag("");
              }}
            >
              Add tag
            </Button>
          </>
        }
      >
        <InputText
          label="Tag"
          value={tag}
          autoFocus
          placeholder="Weekly"
          onChange={(e) => setTag(e.target.value)}
          description={`Goes on ${plural(ids.length, "client")}.`}
        />
      </Modal>
      <Modal
        title={`Change ${plural(confirm?.input.ids.length ?? 0, "client")}?`}
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variation={confirm?.input.action === "deactivate" ? "destructive" : "work"}
              loading={bulk.isPending}
              onClick={() => confirm && run({ ...confirm.input, confirm: confirm.token }, confirm.undo)}
            >
              Yes, change {confirm?.input.ids.length ?? 0}
            </Button>
          </>
        }
      >
        <p>{confirm?.message}</p>
        <p className="muted">More than {BULK_LIMIT} at once always asks first, so a slip does not change the whole list.</p>
      </Modal>
    </Page>
  );
}

/**
 * The four tiles: new leads and new clients next to the 30 days before (Jobber), how many are on a plan (the old Steward's
 * "converted to recurring"), and who may leave. The last two filter the list.
 */
function ClientTiles({ s, show, onShow }: { s: ClientSummary | undefined; show: Show | undefined; onShow: (v: Show) => void }) {
  const wait = <span className="skeleton inline" />;
  const leads = s ? trend(s.leads30, s.leadsBefore30) : null;
  const made = s ? trend(s.new30, s.newBefore30) : null;
  const pct = s?.active ? Math.round((s.onPlan / s.active) * 100) : 0;
  return (
    <div className="tiles">
      <div className="tile">
        <span className="label">New leads</span>
        <span className="value">{s ? s.leads30 : wait}</span>
        <span className="hint">Past 30 days, in the Pipeline</span>
        {leads ? <span className={`hint ${leads.cls}`}>{leads.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">New clients</span>
        <span className="value">{s ? s.new30 : wait}</span>
        <span className="hint">Past 30 days{s ? `, ${String(s.newThisYear)} this year` : ""}</span>
        {made ? <span className={`hint ${made.cls}`}>{made.text}</span> : null}
      </div>
      <button type="button" className="tile tile-button" aria-pressed={show === "plan"} onClick={() => onShow("plan")}>
        <span className="label">On a plan</span>
        <span className="value">{s ? `${String(pct)}%` : wait}</span>
        <span className="hint">{s ? `${String(s.onPlan)} of ${String(s.active)} active clients repeat` : ""}</span>
      </button>
      <button type="button" className="tile tile-button" aria-pressed={show === "at_risk"} onClick={() => onShow("at_risk")}>
        <span className="label">At risk</span>
        <span className={`value${s?.atRisk ? " critical" : ""}`}>{s ? s.atRisk : wait}</span>
        <span className="hint">Complaints, low ratings, late bills, a crew that keeps changing</span>
      </button>
    </div>
  );
}

// One client ---------------------------------------------------------------------------------------------------------

export function ClientPage({ id }: { id: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const client = useClient(id);
  const reactivate = useReactivateClient();
  const cardLink = useCardLink();
  const [sheet, setSheet] = useState<"deactivate" | "merge" | "offer" | null>(null);
  const [tab, setTab] = useState<"info" | "talk">("info");
  const startThread = useStartThread();
  const [openThread, setOpenThread] = useState<string | null>(null);
  const phone = usePhone();
  const c = client.data;
  if (client.error)
    return (
      <Page title="Client">
        <LoadError error={client.error} retry={() => void client.refetch()} />
      </Page>
    );
  if (!c)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("client.update");
  const status = !c.active ? (
    <StatusLabel status="inactive">
      {c.inactiveReason ? `Not active · ${INACTIVE_REASON_WORDS[c.inactiveReason]}` : "Not active"}
    </StatusLabel>
  ) : c.atRisk ? (
    <span title={c.atRisk}>
      <StatusLabel status="critical">At risk</StatusLabel>
    </span>
  ) : null;
  // They reviewed us on Google (matched by name), so the review request skips them.
  const gr = c.googleReview;
  const reviewed = gr ? (
    <span title={gr.at ? `Reviewed on Google ${shortDate(gr.at.slice(0, 10))}` : "Reviewed on Google"}>
      <StatusLabel status="success">
        {gr.stars ? `Google review ${String(gr.stars)} ${gr.stars === 1 ? "star" : "stars"}` : "Reviewed on Google"}
      </StatusLabel>
    </span>
  ) : null;
  const facts = [
    c.kind === "business" ? "Business" : "Home",
    `Client since ${shortDate(c.createdAt.slice(0, 10))}`,
    c.lifetimeCents ? `Paid ${moneyExact(c.lifetimeCents)} so far` : null,
    c.active && c.atRisk ? c.atRisk : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const newJob =
    can("job.update") && c.active ? (
      <Button icon="plus" onClick={() => void navigate({ to: "/jobs/new", search: { clientId: c.id } })}>
        New job
      </Button>
    ) : null;
  const leadWords = c.leadSource ? (LEAD_WORDS[c.leadSource] ?? c.leadSource) : null;
  // Jobber's envelope and bubble: open this client's email or text conversation, ready to write.
  const talk = (channel: "sms" | "email") =>
    startThread.mutate(
      { clientId: c.id, channel },
      {
        onSuccess: (r) => {
          setOpenThread(r.id);
          setTab("talk");
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  const talkBtns =
    editable && (c.phone || c.email) ? (
      <>
        {c.email ? <IconButton icon="mail" label="Email" onClick={() => talk("email")} /> : null}
        {c.phone ? <IconButton icon="message" label="Text" onClick={() => talk("sms")} /> : null}
      </>
    ) : null;
  const editBtn = editable ? (
    <Button type="secondary" icon="edit" onClick={() => void navigate({ to: "/clients/$id/edit", params: { id: c.id } })}>
      Edit
    </Button>
  ) : null;
  // Offer a plan: a one-time home client gets their prices and a link to book their own schedule (owner, 2026-11-02).
  const offers = company.trade === "cleaning" && c.kind === "home" && can("quote.update");
  const moreMenu =
    editable || can("client.view_hub") || can("dispute.read") || offers ? (
      <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
        {(close) => (
          <>
            {offers ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setSheet("offer");
                }}
              >
                Offer a plan
              </button>
            ) : null}
            {can("dispute.read") ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  void navigate({ to: "/clients/$id/dispute", params: { id: c.id } });
                }}
              >
                Dispute file
              </button>
            ) : null}
            {can("client.view_hub") ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  void navigate({ to: "/clients/$id/as-client", params: { id: c.id } });
                }}
              >
                View as client
              </button>
            ) : null}
            {!editable ? null : c.active ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setSheet("deactivate");
                }}
              >
                Set not active
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  reactivate.mutate(c.id, {
                    onSuccess: () => toast(`${c.name} is active again`),
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  });
                }}
              >
                Make active again
              </button>
            )}
            {can("client.merge") ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setSheet("merge");
                }}
              >
                Merge with another client
              </button>
            ) : null}
            {can("invoice.update") && c.active ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    cardLink.mutate(
                      { clientId: c.id, send: true },
                      {
                        onSuccess: () => toast(`Card link texted to ${c.name}. Nothing is charged.`),
                        onError: (e) => toast(errText(e), { kind: "error" }),
                      },
                    );
                  }}
                >
                  Text a link to save a card
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    cardLink.mutate(
                      { clientId: c.id, send: false },
                      {
                        onSuccess: (r) =>
                          void navigator.clipboard.writeText(r.url).then(
                            () => toast("Card link copied"),
                            () => toast(r.url),
                          ),
                        onError: (e) => toast(errText(e), { kind: "error" }),
                      },
                    );
                  }}
                >
                  Copy a link to save a card
                </button>
              </>
            ) : null}
          </>
        )}
      </Menu>
    ) : null;
  const banners = (
    <>
      <NextStep freshKey={`client:${c.id}`} title={`${c.name} is added. What comes next?`}>
        {can("quote.update") ? (
          <Button
            type="secondary"
            size="small"
            icon="quotes"
            onClick={() => void navigate({ to: "/quotes/new", search: { clientId: c.id } })}
          >
            Send a quote
          </Button>
        ) : null}
        {can("job.update") ? (
          <Button
            type="secondary"
            size="small"
            icon="jobs"
            onClick={() => void navigate({ to: "/jobs/new", search: { clientId: c.id } })}
          >
            Book the work
          </Button>
        ) : null}
      </NextStep>
      {!c.active && c.inactiveNote ? <Banner type="warning">{c.inactiveNote}</Banner> : null}
      {c.officeNotes ? (
        <Banner type="notice">
          <b>Office note.</b> {c.officeNotes}
        </Banner>
      ) : null}
    </>
  );
  // Jobber's two tabs: everything about the client, and every text and email with them.
  const tabs = can("client.read") ? (
    <div className="segmented page-tabs" role="tablist" aria-label="Client page">
      <button type="button" role="tab" aria-selected={tab === "info"} onClick={() => setTab("info")}>
        Client information
      </button>
      <button type="button" role="tab" aria-selected={tab === "talk"} onClick={() => setTab("talk")}>
        Communication
      </button>
    </div>
  ) : null;
  const sheets = (
    <>
      <DeactivateSheet c={c} open={sheet === "deactivate"} onClose={() => setSheet(null)} />
      {sheet === "offer" ? <PlanOfferSend clientId={c.id} onClose={() => setSheet(null)} /> : null}
      {can("client.merge") ? <MergeSheet c={c} open={sheet === "merge"} onClose={() => setSheet(null)} /> : null}
    </>
  );

  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/clients" }) }}
        status={
          <>
            {status ?? <StatusLabel status="success">Active</StatusLabel>}
            {reviewed}
          </>
        }
        title={c.name}
        sub={facts}
        phone={c.phone}
        email={c.email}
        facts={[
          ...(c.address
            ? [
                {
                  key: "where",
                  icon: "pin" as const,
                  main: c.address,
                  sub: c.properties.length > 1 ? `Directions · ${String(c.properties.length)} properties` : "Directions",
                  directions: mapsLine(c),
                },
              ]
            : []),
          {
            key: "visits",
            icon: "schedule" as const,
            main: c.nextVisit ? `Next visit ${shortDate(c.nextVisit)}` : "Nothing booked",
            sub: [
              c.lastVisit ? `Last visit ${shortDate(c.lastVisit)}` : null,
              c.activeJobs ? `${String(c.activeJobs)} active ${c.activeJobs === 1 ? "job" : "jobs"}` : null,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        ]}
        actions={
          <>
            {newJob}
            {editBtn}
            {moreMenu}
          </>
        }
      >
        {banners}
        {tabs}
        {tab === "talk" ? (
          <ClientInbox clientId={c.id} />
        ) : (
          <>
            <WorkOverview c={c} />
            <ClientSchedule c={c} />
            <Contacts c={c} editable={editable} />
            <Properties c={c} editable={editable} trade={company.trade} />
            {can("invoice.read") ? (
              <>
                <ClientBilling c={c} />
                <PaymentMethods c={c} />
              </>
            ) : null}
            <NotesCard on="clients" id={c.id} notes={c.notesList} editable={editable} />
            <FilesCard on="clients" id={c.id} files={c.files} editable={editable} title="Files" />
            <RecentPricing c={c} />
            <ClientFacts c={c} leadWords={leadWords} />
          </>
        )}
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={c.name}
      titleMetaData={
        <>
          {status}
          {reviewed}
        </>
      }
      subtitle={facts}
      primaryAction={<CreateForClient c={c} />}
      secondaryAction={
        <>
          {talkBtns}
          {editBtn}
        </>
      }
      moreActions={moreMenu}
    >
      <ClientFacts c={c} leadWords={leadWords} />
      {banners}
      {tabs}
      {tab === "talk" ? (
        <ClientInbox key={openThread ?? "all"} clientId={c.id} open={openThread} />
      ) : (
        <div className="stack">
          <WorkOverview c={c} />
          <div className="home-grid">
            <div className="stack">
              <Contacts c={c} editable={editable} />
              <NotesCard on="clients" id={c.id} notes={c.notesList} editable={editable} />
            </div>
            <div className="stack">
              <Properties c={c} editable={editable} trade={company.trade} />
              <FilesCard on="clients" id={c.id} files={c.files} editable={editable} title="Files" />
            </div>
          </div>
          {can("invoice.read") ? (
            <>
              <ClientBilling c={c} />
              <PaymentMethods c={c} />
            </>
          ) : null}
          <ClientSchedule c={c} />
          <RecentPricing c={c} />
        </div>
      )}
      {sheets}
    </Page>
  );
}

/** The client's address with its city, state and ZIP from the matching property, so the maps app can find it. */
function mapsLine(c: ClientDetail): string {
  const home = c.properties.find((p) => p.address === c.address);
  return home ? fullAddress(home.address, home) : (c.address ?? "");
}

function Contacts({ c, editable }: { c: ClientDetail; editable: boolean }) {
  const add = useAddContact();
  const remove = useRemoveContact();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<"phone" | "email">("phone");
  const [type, setType] = useState<ContactType>("cell");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const pickKind = (k: "phone" | "email") => {
    setKind(k);
    setType(k === "phone" ? "cell" : "billing");
  };
  return (
    <Card
      title="Contact"
      action={
        editable && !adding ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setAdding(true)}>
            Add
          </Button>
        ) : null
      }
      flush
    >
      <div className="todo">
        {c.contacts.map((x) => (
          <div className="item" key={x.id}>
            <Icon name={x.kind === "phone" ? "phone" : "mail"} />
            <div className="what">
              <a href={x.kind === "phone" ? `tel:${x.value}` : `mailto:${x.value}`}>
                {x.kind === "phone" ? (phoneText(x.value) ?? x.value) : x.value}
              </a>
              <span>
                {[
                  x.type ? CONTACT_TYPE_WORDS[x.type] : null,
                  x.label,
                  x.isMain ? "Main" : null,
                  x.optedOut ? "Said stop to texts" : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || (x.kind === "phone" ? "Phone" : "Email")}
              </span>
            </div>
            {editable && !x.isMain ? (
              <IconButton
                icon="trash"
                label="Remove"
                size={18}
                onClick={() =>
                  remove.mutate({ clientId: c.id, id: x.id }, { onError: (e) => toast(errText(e), { kind: "error" }) })
                }
              />
            ) : null}
          </div>
        ))}
        {!c.contacts.length && !adding ? <div className="card-empty muted">No phone or email yet.</div> : null}
      </div>
      {adding ? (
        <form
          className="note-form"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate(
              {
                clientId: c.id,
                contact: { kind, type, value: value.trim(), ...(label.trim() ? { label: label.trim() } : {}), isMain: false },
              },
              {
                onSuccess: () => {
                  setAdding(false);
                  setValue("");
                  setLabel("");
                },
                onError: (er) => toast(errText(er), { kind: "error" }),
              },
            );
          }}
        >
          <Choice
            label="Kind"
            value={kind}
            onChange={pickKind}
            options={[
              { value: "phone", label: "Phone" },
              { value: "email", label: "Email" },
            ]}
          />
          <ContactTypeSelect kind={kind} value={type} onChange={setType} />
          <InputText
            label={kind === "phone" ? "Phone" : "Email"}
            value={value}
            autoFocus
            type={kind === "phone" ? "tel" : "email"}
            onChange={(e) => setValue(e.target.value)}
          />
          <InputText label="Name" value={label} placeholder="Mike, tenant" onChange={(e) => setLabel(e.target.value)} />
          <div className="row end">
            <Button type="tertiary" size="small" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button size="small" submit loading={add.isPending} disabled={!value.trim()}>
              Save
            </Button>
          </div>
        </form>
      ) : null}
      <div className="card-foot muted">
        {c.prefers === "email" ? "Likes email best" : "Likes texts best"}
        {c.noMarketing ? " · No marketing" : ""}
      </div>
    </Card>
  );
}

function Properties({ c, editable, trade }: { c: ClientDetail; editable: boolean; trade: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PropertyRow | null>(null);
  return (
    <Card
      title={c.properties.length > 1 ? "Properties" : "Property"}
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setAdding(true)}>
            Add
          </Button>
        ) : null
      }
      flush
    >
      {c.properties.length ? (
        <div className="todo">
          {c.properties.map((p) => (
            <div key={p.id} className="fold">
              <button
                type="button"
                className="item fold-head"
                aria-expanded={open === p.id}
                onClick={() => setOpen(open === p.id ? null : p.id)}
              >
                <Icon name="pin" />
                <div className="what">
                  <b>{p.label ?? p.address}</b>
                  <span>
                    {[p.label ? p.address : null, p.summary || null].filter(Boolean).join(" · ") ||
                      [p.city, p.state].filter(Boolean).join(", ")}
                  </span>
                </div>
                <Icon name={open === p.id ? "chevronDown" : "chevronRight"} size={18} />
              </button>
              {open === p.id ? (
                <dl className="details fold-body">
                  <div>
                    <dt>Address</dt>
                    <dd>{[p.address, p.city, [p.state, p.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</dd>
                  </div>
                  {trade === "cleaning" ? (
                    <div>
                      <dt>Size</dt>
                      <dd>{p.summary || <span className="muted">Not set</span>}</dd>
                    </div>
                  ) : trade === "lawn" ? (
                    <div>
                      <dt>Lawn</dt>
                      <dd>
                        {p.lawnSqft ? `${p.lawnSqft.toLocaleString("en-US")} sq ft` : <span className="muted">Not set</span>}
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Gate and alarm</dt>
                    <dd>{p.accessNotes ?? <span className="muted">None</span>}</dd>
                  </div>
                  {p.notes ? (
                    <div>
                      <dt>Notes</dt>
                      <dd>{p.notes}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt />
                    <dd>
                      <a
                        href={`https://maps.apple.com/?q=${encodeURIComponent([p.address, p.city, p.state].filter(Boolean).join(", "))}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in maps
                      </a>
                      {editable ? (
                        <Button type="tertiary" size="small" icon="edit" onClick={() => setEditing(p)}>
                          Edit
                        </Button>
                      ) : null}
                    </dd>
                  </div>
                </dl>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="card-empty muted">No address yet.</div>
      )}
      {adding || editing ? (
        <PropertySheet
          clientId={c.id}
          property={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          trade={trade}
          business={c.kind === "business"}
        />
      ) : null}
    </Card>
  );
}

/** Add a property, or change one (property set): the same address box as a new client, in parts. */
function PropertySheet({
  clientId,
  property,
  onClose,
  trade,
  business,
}: {
  clientId: string;
  property: PropertyRow | null;
  onClose: () => void;
  trade: string;
  business: boolean;
}) {
  const save = useSaveProperty();
  const toast = useToast();
  const [place, setPlace] = useState<PlaceDraft>(() => (property ? placeOf(property) : EMPTY_PLACE));
  const [label, setLabel] = useState(property?.label ?? "");
  const submit = (e?: SubmitEvent) => {
    e?.preventDefault();
    if (place.street1.trim().length < 3) return;
    save.mutate(
      { clientId, ...(property ? { id: property.id } : {}), property: placeOut(place, label, property) },
      {
        onSuccess: () => {
          onClose();
          toast(property ? "Address saved" : "Property added");
        },
        onError: (er) => toast(errText(er), { kind: "error" }),
      },
    );
  };
  return (
    <Modal
      title={property ? "Change the address" : "Add a property"}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={place.street1.trim().length < 3} onClick={() => submit()}>
            Save property
          </Button>
        </>
      }
    >
      <form className="client-form" autoComplete="on" onSubmit={submit}>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        <PlaceFields p={place} set={setPlace} trade={trade} business={business} />
        <div className="span-6">
          <InputText
            label="Name for it"
            placeholder="Rental, Mom's house"
            autoComplete="off"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
      </form>
    </Modal>
  );
}

export interface PropertyDraft {
  label: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  beds: string;
  baths: string;
  rooms: string;
  sqft: string;
  lawnSqft: string;
  accessNotes: string;
}
export const EMPTY_PROPERTY: PropertyDraft = {
  label: "",
  address: "",
  city: "New Orleans",
  state: "LA",
  zip: "",
  beds: "",
  baths: "",
  rooms: "",
  sqft: "",
  lawnSqft: "",
  accessNotes: "",
};
const num = (s: string) => (s.trim() && Number.isFinite(Number(s)) ? Number(s) : null);
const txt = (s: string) => s.trim() || undefined;
export function propertyOut(p: PropertyDraft) {
  return {
    label: txt(p.label),
    address: p.address.trim(),
    city: txt(p.city),
    state: txt(p.state.toUpperCase()),
    zip: txt(p.zip),
    beds: num(p.beds),
    baths: num(p.baths),
    rooms: num(p.rooms),
    sqft: num(p.sqft),
    lawnSqft: num(p.lawnSqft),
    accessNotes: txt(p.accessNotes),
  };
}

function DeactivateSheet({ c, open, onClose }: { c: ClientDetail; open: boolean; onClose: () => void }) {
  const deactivate = useDeactivateClient();
  const reactivate = useReactivateClient();
  const toast = useToast();
  const [reason, setReason] = useState<InactiveReason>("cancelled");
  const [note, setNote] = useState("");
  return (
    <Modal
      title={`Set ${c.name} to not active?`}
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variation="destructive"
            loading={deactivate.isPending}
            onClick={() =>
              deactivate.mutate(
                { id: c.id, body: { reason, ...(note.trim() ? { note: note.trim() } : {}) } },
                {
                  onSuccess: () => {
                    onClose();
                    toast(`${c.name} set to not active`, { undo: () => reactivate.mutate(c.id) });
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Set not active
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">
          Their history stays. They drop off the lists and nothing new gets booked. You can make them active again any time.
        </p>
        <Combobox
          label="Why"
          value={reason}
          onChange={(v) => setReason(v as InactiveReason)}
          options={INACTIVE_REASONS.map((r) => ({ value: r, label: INACTIVE_REASON_WORDS[r] }))}
        />
        <InputText
          label="Note"
          value={note}
          placeholder="Moved to Baton Rouge in March"
          onChange={(e) => setNote(e.target.value)}
        />
        {c.activeJobs ? (
          <Banner type="warning">{plural(c.activeJobs, "active job")} stay on the schedule until you end them.</Banner>
        ) : null}
      </div>
    </Modal>
  );
}

function MergeSheet({ c, open, onClose }: { c: ClientDetail; open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const merge = useMergeClient();
  const others = useClients(company.id, { status: "all" }, open);
  const [keep, setKeep] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const keepRow = others.data?.items.find((x) => x.id === keep);
  return (
    <Modal
      title="Merge two clients"
      open={open}
      onClose={() => {
        setProblem(null);
        onClose();
      }}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="merge"
            disabled={!keep}
            loading={merge.isPending}
            onClick={() =>
              merge.mutate(
                { id: c.id, into: keep },
                {
                  onSuccess: (a) => {
                    onClose();
                    const m = a.moved;
                    toast(
                      `Merged. Moved ${plural(m.jobs, "job")}, ${plural(m.contacts, "contact")} and ${plural(m.notes, "note")}.`,
                    );
                    void navigate({ to: "/clients/$id", params: { id: a.kept } });
                  },
                  onError: (e) => setProblem(errText(e)),
                },
              )
            }
          >
            Merge
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>
          <b>{c.name}</b> gets closed. Its jobs, visits, contacts, notes and files move to the client you keep.
        </p>
        <Combobox
          label="Client to keep"
          value={keep}
          onChange={(v) => {
            setKeep(v);
            setProblem(null);
          }}
          options={(others.data?.items ?? [])
            .filter((x) => x.id !== c.id)
            .map((x) => ({ value: x.id, label: `${x.name}${x.address ? ` · ${x.address}` : ""}` }))}
        />
        {keepRow ? (
          <p className="muted">
            {keepRow.name} keeps its own name, phone and email. Matching phones and addresses are not copied twice.
          </p>
        ) : null}
        {problem ? <Banner type="error">{problem}</Banner> : null}
        <p className="muted">This cannot be undone from here.</p>
      </div>
    </Modal>
  );
}

// Form ---------------------------------------------------------------------------------------------------------------

/** What a new client still needs, in words, or null when it can be saved. Last name and email are asked for but not a must. */
export function newClientMissing(f: { firstName: string; phone: string }, p: PropertyDraft): string | null {
  const need = [
    f.firstName.trim() ? null : "first name",
    f.phone.trim() ? null : "phone",
    p.address.trim() ? null : "street address",
  ].filter((x): x is string => x !== null);
  if (!need.length) return null;
  const last = need.pop() ?? "";
  return `A new client needs a ${need.length ? `${need.join(", ")} and ${last}` : last}.`;
}

/** Jobber's address box: one field per line with the browser's autofill names, plus the place's size folded below. */
export interface PlaceDraft {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  beds: string;
  baths: string;
  rooms: string;
  sqft: string;
  lawnSqft: string;
  codes: string;
}
export const EMPTY_PLACE: PlaceDraft = {
  street1: "",
  street2: "",
  city: EMPTY_PROPERTY.city,
  state: EMPTY_PROPERTY.state,
  zip: "",
  country: "United States",
  beds: "",
  baths: "",
  rooms: "",
  sqft: "",
  lawnSqft: "",
  codes: "",
};
/** The browser fills "LOUISIANA" or "Louisiana"; the record keeps the 2 letter code. A typo keeps its first 2 letters. */
const stateOf = (s: string) => stateCode(s) || s.trim().toUpperCase().slice(0, 2);

/** The address box as the property the server takes (Street 2 joins Street 1, state names become the 2 letter code). */
export function placeProperty(p: PlaceDraft): PropertyDraft {
  return {
    ...EMPTY_PROPERTY,
    address: [p.street1.trim(), p.street2.trim()].filter(Boolean).join(", "),
    city: p.city,
    state: stateOf(p.state),
    zip: p.zip,
    beds: p.beds,
    baths: p.baths,
    rooms: p.rooms,
    sqft: p.sqft,
    lawnSqft: p.lawnSqft,
    accessNotes: p.codes,
  };
}

/** A saved property back in the address box. Street 2 was joined to Street 1 with a comma, so it splits there again. */
export function placeOf(p: PropertyRow): PlaceDraft {
  const cut = p.address.indexOf(", ");
  const n = (v: number | null) => (v === null ? "" : String(v));
  return {
    ...EMPTY_PLACE,
    street1: cut < 0 ? p.address : p.address.slice(0, cut),
    street2: cut < 0 ? "" : p.address.slice(cut + 2),
    city: p.city ?? "",
    state: p.state ?? "",
    zip: p.zip ?? "",
    beds: n(p.beds),
    baths: n(p.baths),
    rooms: n(p.rooms),
    sqft: n(p.sqft),
    lawnSqft: n(p.lawnSqft),
    codes: p.accessNotes ?? "",
  };
}

/** The address box as the property to save. A box emptied on a saved property clears it instead of keeping the old words. */
export function placeOut(place: PlaceDraft, label: string, old: PropertyRow | null): PropertyInput {
  const out: PropertyInput = propertyOut({ ...placeProperty(place), label });
  if (old) {
    for (const k of ["label", "city", "state", "zip", "accessNotes"] as const) if (old[k] && !out[k]) out[k] = null;
  }
  return out;
}

/** The address box's fields. Goes inside a `client-form` grid, inside a real form so Chrome fills it. */
export function PlaceFields({
  p,
  set,
  trade,
  business,
  optional,
}: {
  p: PlaceDraft;
  set: (p: PlaceDraft) => void;
  trade: string;
  business: boolean;
  /** A saved client with no address yet may be saved without one. */
  optional?: boolean;
}) {
  const f = (k: keyof PlaceDraft) => ({
    value: p[k],
    onChange: (e: { target: { value: string } }) => set({ ...p, [k]: e.target.value }),
  });
  const count = (k: "beds" | "baths" | "rooms") => ({ value: p[k], onChange: (v: string) => set({ ...p, [k]: v }) });
  const sized = trade === "cleaning" || trade === "lawn";
  const summary = [
    p.beds && `${p.beds} bed`,
    p.baths && `${p.baths} bath`,
    p.rooms && (business ? `${p.rooms} rooms` : `${p.rooms} other ${p.rooms === "1" ? "room" : "rooms"}`),
    p.sqft && `${p.sqft} sq ft`,
    p.lawnSqft && `${p.lawnSqft} sq ft lawn`,
    p.codes && "codes saved",
  ].filter(Boolean);
  return (
    <>
      <div className="span-6">
        <InputText label="Street 1" name="address-line1" autoComplete="address-line1" required={!optional} {...f("street1")} />
      </div>
      <div className="span-6">
        <InputText
          label="Street 2"
          name="address-line2"
          autoComplete="address-line2"
          placeholder="Apartment, suite, unit"
          {...f("street2")}
        />
      </div>
      <div className="span-3 wide-phone">
        <InputText label="City" name="address-level2" autoComplete="address-level2" {...f("city")} />
      </div>
      <div className="span-3">
        <InputText
          label="State"
          name="address-level1"
          autoComplete="address-level1"
          {...f("state")}
          onBlur={() => set({ ...p, state: stateOf(p.state) })}
        />
      </div>
      <div className="span-3">
        <InputText label="ZIP code" name="postal-code" autoComplete="postal-code" inputMode="numeric" {...f("zip")} />
      </div>
      <div className="span-3 wide-phone">
        <FormField label="Country">
          <select className="select" name="country" autoComplete="country-name" {...f("country")}>
            <option>United States</option>
          </select>
        </FormField>
      </div>
      <details className="span-6 fold">
        <summary>
          {sized ? "Size and gate codes" : "Gate and alarm codes"}
          {summary.length ? <span className="muted"> {summary.join(", ")}</span> : null}
        </summary>
        <div className="form-grid">
          {trade === "cleaning" ? (
            business ? (
              <>
                <InputCount label="Rooms" max={500} {...count("rooms")} />
                <InputText label="Square feet" inputMode="numeric" autoComplete="off" {...f("sqft")} />
              </>
            ) : (
              <>
                <InputCount label="Bedrooms" max={20} {...count("beds")} />
                <InputCount label="Bathrooms" max={20} decimal {...count("baths")} />
                <InputCount label="Other rooms" max={50} {...count("rooms")} />
                <InputText label="Square feet" inputMode="numeric" autoComplete="off" {...f("sqft")} />
              </>
            )
          ) : trade === "lawn" ? (
            <InputText label="Lawn size" suffix="sq ft" inputMode="numeric" autoComplete="off" {...f("lawnSqft")} />
          ) : null}
          <InputText
            label="Gate and alarm codes"
            wide
            autoComplete="off"
            description="Only the office sees these."
            {...f("codes")}
          />
        </div>
      </details>
    </>
  );
}

/** One part of a long form, Jobber style: the title and a line on the left, the fields on the right. */
/** What a phone or email is for: cell, office, billing. Phones and emails each offer their own list. */
function ContactTypeSelect({
  kind,
  value,
  onChange,
}: {
  kind: "phone" | "email";
  value: ContactType;
  onChange: (v: ContactType) => void;
}) {
  return (
    <FormField label="Type">
      <select className="select" value={value} onChange={(e) => onChange(e.target.value as ContactType)}>
        {(kind === "phone" ? PHONE_TYPES : EMAIL_TYPES).map((t) => (
          <option key={t} value={t}>
            {CONTACT_TYPE_WORDS[t]}
          </option>
        ))}
      </select>
    </FormField>
  );
}

/** An extra phone or email on the client form. New ones have no id until saved. */
interface ContactDraft {
  key: string;
  id?: string;
  kind: "phone" | "email";
  type: ContactType;
  value: string;
  label: string;
}

export function contactDraftOf(x: ContactRow): ContactDraft {
  return {
    key: x.id,
    id: x.id,
    kind: x.kind,
    type: x.type ?? (x.kind === "phone" ? "cell" : "billing"),
    value: x.kind === "phone" ? (phoneText(x.value) ?? x.value) : x.value,
    label: x.label ?? "",
  };
}

/** The filled rows, ready to save, or what is wrong with one. */
export function contactsOut(list: ContactDraft[]): { contacts: Omit<ContactDraft, "key">[] } | { error: string } {
  const filled = list.filter((x) => x.value.trim());
  const bad = filled.find((x) =>
    x.kind === "email" ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x.value.trim()) : x.value.replace(/\D/g, "").length < 7,
  );
  if (bad) return { error: `The extra ${bad.kind === "email" ? "email" : "phone"} ${bad.value.trim()} does not look right.` };
  return {
    contacts: filled.map(({ key: _key, ...x }) => ({ ...x, value: x.value.trim(), label: x.label.trim() })),
  };
}

/** More phones and emails, each with its type and who it reaches, added and removed right on the form. */
function ExtraContacts({ list, set }: { list: ContactDraft[]; set: (v: ContactDraft[]) => void }) {
  const change = (key: string, p: Partial<ContactDraft>) => set(list.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const add = (kind: "phone" | "email") =>
    set([...list, { key: crypto.randomUUID(), kind, type: kind === "phone" ? "office" : "billing", value: "", label: "" }]);
  return (
    <>
      {list.map((x) => (
        <div className="contact-row" key={x.key}>
          <ContactTypeSelect kind={x.kind} value={x.type} onChange={(type) => change(x.key, { type })} />
          <InputText
            label={x.kind === "phone" ? "Phone number" : "Email"}
            type={x.kind === "phone" ? "tel" : "email"}
            autoComplete="off"
            autoFocus={!x.id && !x.value}
            value={x.value}
            onChange={(e) => change(x.key, { value: e.target.value })}
          />
          <InputText
            label="Name"
            placeholder="Mike, front desk"
            autoComplete="off"
            maxLength={40}
            value={x.label}
            onChange={(e) => change(x.key, { label: e.target.value })}
          />
          <IconButton
            icon="trash"
            label={`Remove ${x.kind === "phone" ? "phone" : "email"}`}
            size={18}
            onClick={() => set(list.filter((y) => y.key !== x.key))}
          />
        </div>
      ))}
      <div className="span-6 row">
        <Button type="tertiary" size="small" icon="plus" onClick={() => add("phone")}>
          Add phone
        </Button>
        <Button type="tertiary" size="small" icon="plus" onClick={() => add("email")}>
          Add email
        </Button>
      </div>
    </>
  );
}

function FormSection({ title, intro, children }: { title: string; intro: ReactNode; children: ReactNode }) {
  return (
    <section className="form-section">
      <div className="form-section-head">
        <h2>{title}</h2>
        <p className="muted">{intro}</p>
      </div>
      <div className="client-form">{children}</div>
    </section>
  );
}

/**
 * Another client with this phone or email, found as the office types, so nobody is added twice (Jobber only catches it
 * later, with Merge clients). Null when there is none.
 */
function useTwin(companyId: string, phone: string, email: string, skip: string | null) {
  const digits = phone.replace(/\D/g, "").slice(-10);
  const mail = /^\S+@\S+\.\S+$/.test(email.trim()) ? email.trim().toLowerCase() : "";
  const byPhone = useClients(companyId, { status: "all", q: digits }, digits.length === 10);
  const byMail = useClients(companyId, { status: "all", q: mail }, Boolean(mail));
  const phoneTwin =
    digits.length === 10
      ? byPhone.data?.items.find((r) => r.id !== skip && (r.phone ?? "").replace(/\D/g, "").endsWith(digits))
      : undefined;
  if (phoneTwin) return { client: phoneTwin, by: "phone number" };
  const mailTwin = mail ? byMail.data?.items.find((r) => r.id !== skip && r.email?.toLowerCase() === mail) : undefined;
  return mailTwin ? { client: mailTwin, by: "email" } : null;
}

export function ClientForm({ id }: { id?: string }) {
  const existing = useClient(id ?? "");
  if (id && !existing.data)
    return <Page title="Edit client">{existing.error ? <LoadError error={existing.error} /> : <Skeleton rows={6} />}</Page>;
  return <ClientFormBody c={id ? existing.data : undefined} />;
}

/**
 * New and Edit client (2026-10-29). Jobber's page, section by section with the browser's autofill names, plus what neither
 * Jobber nor the old Steward did: a warning while typing when the phone or email is already a client's, tags picked from
 * the ones in use, and Save and add a job, so booking is the next step instead of a crowded form (the old Steward asked for
 * the job, price, checklist and billing all on this page).
 */
function ClientFormBody({ c }: { c: ClientDetail | undefined }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateClient();
  const update = useUpdateClient();
  const saveProperty = useSaveProperty();
  const summary = useClientSummary(company.id);
  const [f, setF] = useState({
    title: c?.title ?? "",
    firstName: c?.firstName ?? "",
    lastName: c?.lastName ?? "",
    companyName: c?.companyName ?? "",
    isCompany: c?.isCompany ?? false,
    phone: phoneText(c?.phone ?? null) ?? "",
    email: c?.email ?? "",
    leadSource: c?.leadSource ?? "",
    prefers: c?.prefers ?? "text",
    noMarketing: c?.noMarketing ?? false,
    officeNotes: c?.officeNotes ?? "",
    referredById: c?.referredBy?.id ?? "",
  });
  const [billOn, setBillOn] = useState(Boolean(c?.billing));
  const [billing, setBilling] = useState<AddressDraft>(() => draftOf(c?.billing));
  // Referred by asks for the client who sent them; the list loads only when it shows.
  const askReferrer = f.leadSource === "referral" || Boolean(f.referredById);
  const referrers = useClients(company.id, { status: "active" }, askReferrer);
  const [tags, setTags] = useState<string[]>(c?.tags ?? []);
  const [tagText, setTagText] = useState("");
  const [extras, setExtras] = useState<ContactDraft[]>(() => (c?.contacts ?? []).filter((x) => !x.isMain).map(contactDraftOf));
  // A saved client changes their one property's address here; with more than one, each is changed on their page.
  const home = c?.properties.length === 1 ? c.properties[0]! : null;
  const [placeStart] = useState<PlaceDraft>(() => (home ? placeOf(home) : EMPTY_PLACE));
  const [place, setPlace] = useState<PlaceDraft>(placeStart);
  const [error, setError] = useState<string | null>(null);
  const [then, setThen] = useState<"page" | "job">("page");
  /** Which save button was pressed; read in the submit, which runs before the state above updates. */
  const thenRef = useRef<"page" | "job">("page");
  const press = (v: "page" | "job") => {
    thenRef.current = v;
    setThen(v);
  };
  const twin = useTwin(company.id, f.phone, f.email, c?.id ?? null);
  const named = Boolean(f.companyName.trim());
  const business = named || f.isCompany;
  const set = (k: "firstName" | "lastName" | "companyName" | "phone" | "email" | "title") => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  const addTag = (t: string) => {
    const tag = t.trim().replace(/,$/, "").trim();
    if (tag && !tags.some((x) => x.toLowerCase() === tag.toLowerCase())) setTags([...tags, tag]);
    setTagText("");
  };
  const unusedTags = (summary.data?.tags ?? []).map((t) => t.tag).filter((t) => !tags.includes(t));
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const p = placeProperty(place);
    const bill = billOn ? partsOf(billing) : { parts: null, problem: null };
    const missing = c
      ? !f.firstName.trim() && !f.lastName.trim() && !f.companyName.trim()
        ? "Give a first or last name, or a company name."
        : home && !place.street1.trim()
          ? "Type the street address."
          : null
      : newClientMissing(f, { ...p, address: place.street1 });
    const more = contactsOut(extras);
    const problem = missing ?? (bill.problem ? `Billing address. ${bill.problem}` : null);
    if (problem || "error" in more) {
      setError(problem ?? ("error" in more ? more.error : null));
      return;
    }
    const allTags = tagText.trim() ? [...tags, tagText.trim()] : tags;
    const body = {
      ...(f.title ? { title: f.title as (typeof CLIENT_TITLES)[number] } : { title: null }),
      firstName: txt(f.firstName),
      lastName: txt(f.lastName),
      companyName: txt(f.companyName) ?? null,
      isCompany: business,
      phone: txt(f.phone),
      email: txt(f.email),
      tags: allTags,
      leadSource: (f.leadSource || undefined) as (typeof LEAD_SOURCES)[number] | undefined,
      prefers: f.prefers,
      noMarketing: f.noMarketing,
      officeNotes: txt(f.officeNotes),
      billing: bill.parts,
      referredById: f.referredById || null,
    };
    const done = (x: ClientDetail) => {
      toast(c ? "Client saved" : `${x.name} added`);
      if (!c) markFresh(`client:${x.id}`);
      if (thenRef.current === "job") void navigate({ to: "/jobs/new", search: { clientId: x.id } });
      else void navigate({ to: "/clients/$id", params: { id: x.id } });
    };
    const fail = (er: unknown) => setError(errText(er));
    // The address saves after the client: the one property changed, or a first one added to a client who had none.
    const placeChanged = JSON.stringify(place) !== JSON.stringify(placeStart) && Boolean(place.street1.trim());
    const thenPlace = (x: ClientDetail) =>
      c && placeChanged && (home || !c.properties.length)
        ? saveProperty.mutate(
            { clientId: c.id, ...(home ? { id: home.id } : {}), property: placeOut(place, home?.label ?? "", home) },
            { onSuccess: () => done(x), onError: fail },
          )
        : done(x);
    if (c) update.mutate({ id: c.id, patch: { ...body, contacts: more.contacts } }, { onSuccess: thenPlace, onError: fail });
    else
      create.mutate(
        {
          ...body,
          firstName: f.firstName.trim(),
          phone: f.phone.trim(),
          email: f.email.trim(),
          property: propertyOut(p),
          contacts: more.contacts,
        },
        { onSuccess: done, onError: fail },
      );
  };
  const saving = create.isPending || update.isPending || saveProperty.isPending;
  return (
    <Page title={c ? `Edit ${c.name}` : "New client"}>
      <form className="client-page-form pform" autoComplete="on" onSubmit={submit} aria-label={c ? "Edit client" : "New client"}>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} onClick={() => press("page")} />
        {error ? <Banner type="error">{error}</Banner> : null}
        <FormSection title="Who they are" intro="The main person you talk to. Mark a business, with or without a company name.">
          <div className="span-2 wide-phone">
            <FormField label="Title">
              <select className="select" name="honorific-prefix" autoComplete="honorific-prefix" {...set("title")}>
                <option value="">No title</option>
                {CLIENT_TITLES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="span-2 wide-phone">
            <InputText
              label="First name"
              name="given-name"
              autoComplete="given-name"
              autoFocus={!c}
              required={!c}
              {...set("firstName")}
            />
          </div>
          <div className="span-2 wide-phone">
            <InputText label="Last name" name="family-name" autoComplete="family-name" {...set("lastName")} />
          </div>
          <div className="span-6">
            <InputText
              label="Company name"
              name="organization"
              autoComplete="organization"
              description="Leave blank if the business has no name to show."
              {...set("companyName")}
            />
          </div>
          <label className="span-6 row">
            <Switch checked={business} disabled={named} onChange={(v) => setF({ ...f, isCompany: v })} label="Business client" />
            <span>
              {business
                ? named
                  ? "A business, since it has a company name. Invoices are due in 7 days."
                  : "A business. Invoices are due in 7 days."
                : "Turn on for a business, like an office, store or landlord."}
            </span>
          </label>
        </FormSection>
        <FormSection
          title="How to reach them"
          intro="Invoices and visit reminders go to the main phone and email. Add more, like an office line or a billing email."
        >
          <div className="span-3 wide-phone">
            <InputText label="Phone number" type="tel" name="tel" autoComplete="tel" required={!c} {...set("phone")} />
          </div>
          <div className="span-3 wide-phone">
            <InputText label="Email" type="email" name="email" autoComplete="email" {...set("email")} />
          </div>
          {twin ? (
            <div className="span-6">
              <Banner
                type="warning"
                action={
                  <Button
                    type="secondary"
                    size="small"
                    onClick={() => void navigate({ to: "/clients/$id", params: { id: twin.client.id } })}
                  >
                    Open {twin.client.name}
                  </Button>
                }
              >
                {twin.client.name} already has this {twin.by}
                {twin.client.active ? "" : " (not active)"}. Open their page so they are not added twice.
              </Banner>
            </div>
          ) : null}
          <ExtraContacts list={extras} set={setExtras} />
          <div className="span-3 wide-phone">
            <Choice
              label="They like to hear from you by"
              value={f.prefers}
              onChange={(v) => setF({ ...f, prefers: v })}
              options={[
                { value: "text", label: "Text" },
                { value: "email", label: "Email" },
              ]}
            />
          </div>
          <label className="span-6 row">
            <Switch checked={!f.noMarketing} onChange={(v) => setF({ ...f, noMarketing: !v })} label="Marketing" />
            <span>Send them offers and news. Turn off if they asked you to stop.</span>
          </label>
        </FormSection>
        <FormSection
          title="Where the work is"
          intro={
            !c
              ? "Their home or business. Add more places on their page later."
              : home || !c.properties.length
                ? "Their home or business, and where bills go."
                : "Their places, and where bills go."
          }
        >
          {c && c.properties.length > 1 ? (
            <div className="span-6 place-list">
              {c.properties.map((p) => (
                <div key={p.id} className="row">
                  <Icon name="pin" size={16} />
                  <span>
                    {p.label ? <b>{p.label} </b> : null}
                    {fullAddress(p.address, p)}
                  </span>
                </div>
              ))}
              <span className="description">Change each one on their page, under Properties.</span>
            </div>
          ) : (
            <PlaceFields p={place} set={setPlace} trade={company.trade} business={business} optional={Boolean(c && !home)} />
          )}
          <label className="span-6 row">
            <Switch checked={billOn} onChange={setBillOn} label="Bills go to another address" />
            <span>
              {billOn
                ? "Billing address. Invoices go here, not to the property."
                : "Turn on when invoices go somewhere other than the property, like a PO Box."}
            </span>
          </label>
          {billOn ? (
            <div className="span-6">
              <AddressFields value={billing} onChange={setBilling} kind="billing" />
            </div>
          ) : null}
        </FormSection>
        <FormSection title="About them" intro="How they found you, tags to group them, and a note only the office sees.">
          <div className="span-3 wide-phone">
            <Combobox
              label="How they found you"
              value={f.leadSource}
              onChange={(v) => setF({ ...f, leadSource: v })}
              options={LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_WORDS[s] ?? s }))}
            />
          </div>
          {askReferrer ? (
            <div className="span-3 wide-phone">
              <Combobox
                label="Referred by"
                value={f.referredById}
                onChange={(v) => setF({ ...f, referredById: v })}
                options={(referrers.data?.items ?? [])
                  .filter((x) => x.id !== c?.id)
                  .map((x) => ({ value: x.id, label: x.name, ...(x.address ? { sub: x.address } : {}) }))}
              />
            </div>
          ) : null}
          <div className="span-3 wide-phone">
            <InputText
              label="Tags"
              placeholder="Type a tag, then Enter"
              autoComplete="off"
              value={tagText}
              onChange={(e) => (e.target.value.endsWith(",") ? addTag(e.target.value) : setTagText(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagText.trim()) {
                  e.preventDefault();
                  addTag(tagText);
                } else if (e.key === "Backspace" && !tagText && tags.length) setTags(tags.slice(0, -1));
              }}
            />
          </div>
          {tags.length || unusedTags.length ? (
            <div className="span-6 tag-picker">
              {tags.map((t) => (
                <button key={t} type="button" className="tag on" onClick={() => setTags(tags.filter((x) => x !== t))}>
                  {t} <Icon name="close" size={12} />
                </button>
              ))}
              {unusedTags.slice(0, 8).map((t) => (
                <button key={t} type="button" className="tag add" onClick={() => addTag(t)}>
                  <Icon name="plus" size={12} /> {t}
                </button>
              ))}
            </div>
          ) : null}
          <div className="span-6">
            <FormField label="Office note" description="Shows at the top of their page. The crew does not see it.">
              <textarea rows={3} value={f.officeNotes} onChange={(e) => setF({ ...f, officeNotes: e.target.value })} />
            </FormField>
          </div>
        </FormSection>
        <div className="form-foot form-bar">
          <Button type="tertiary" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <div className="spacer" />
          {!c && can("job.update") ? (
            <Button type="secondary" submit loading={saving && then === "job"} onClick={() => press("job")}>
              Save and add a job
            </Button>
          ) : null}
          <Button submit loading={saving && then === "page"} onClick={() => press("page")}>
            Save client
          </Button>
        </div>
      </form>
    </Page>
  );
}
